<template>
  <transition
    enter-active-class="transition duration-200 ease-out"
    enter-from-class="transform scale-105 opacity-0"
    enter-to-class="transform scale-100 opacity-100"
  >
    <div
      v-if="isOpen || renderContentOnMount"
      v-show="isOpen"
      class="fixed inset-0 z-[100] flex items-start overflow-y-auto bg-black bg-opacity-50"
      :class="[alignCenter ? 'md:items-center' : 'sm:pt-14']"
    >
      <div
        ref="modal"
        class="relative z-50 mx-auto min-h-full w-full !max-w-full shadow-lg md:min-h-0"
        :class="[
          alignCenter ? 'my-auto' : 'mb-auto',
          styles.background[background],
          styles.roundings[rounded],
        ]"
        :style="{ width, height }"
      >
        <span
          v-if="closeButton"
          class="absolute z-50"
          :class="[closeButtonStyles]"
        >
          <button
            aria-label="close"
            class="flex items-center p-3"
            :class="{ 'mr-3': !width }"
            @click="close"
          >
            <span
              v-if="$slots.closeButton && isDesktop"
              class="title mr-2 text-sm uppercase"
            >
              <slot name="closeButton"></slot>
            </span>

            <UiIcon icon="close" class="h-6 w-6" />
          </button>
        </span>

        <slot name="default"></slot>
      </div>
    </div>
  </transition>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';
import { onClickOutside } from '@vueuse/core';
import { useBreakpoint, useBodyScrollLock } from '~/composables';

interface Props {
  modelValue?: boolean;
  width?: string;
  height?: string;
  closeButton?: string | boolean;
  rounded?: 'none' | 'lg' | '2xl';
  background?: 'white' | 'gray' | 'transparent';
  alignCenter?: boolean;
  renderContentOnMount?: boolean;
}
interface Emits {
  (e: 'update:modelValue', data: Props['modelValue']): void;
}
const props = withDefaults(defineProps<Props>(), {
  modelValue: false,
  width: '',
  height: 'auto',
  closeButton: 'default',
  rounded: 'lg',
  background: 'white',
  alignCenter: false,
  renderContentOnMount: false,
});

const emit = defineEmits<Emits>();

const styles = {
  background: {
    white: 'bg-white',
    gray: 'bg-gray-lighten3',
    transparent: '',
  },
  roundings: {
    none: '',
    lg: 'md:rounded-lg',
    '2xl': 'md:rounded-2xl',
  },
  button: {
    outside: 'right-0 -top-[3.25rem] text-white',
    default: 'top-3 right-3 md:top-6 md:right-6',
  },
};

type BtnStyle = keyof typeof styles.button;

const { isBiggerThanBreakpoint: isDesktop } = useBreakpoint('sm');
const modal = ref();
const isOpen = computed({
  get() {
    return props.modelValue;
  },
  set(newValue) {
    emit('update:modelValue', newValue);
  },
});

const closeButtonStyles = computed(() =>
  props.closeButton
    ? (styles.button[props.closeButton as BtnStyle] ?? styles.button.default)
    : null,
);

useBodyScrollLock(isOpen);

const close = () => (isOpen.value = false);
const open = () => (isOpen.value = true);

defineExpose({ open });

onClickOutside(modal as any, () => close());
</script>
